import { h, Component } from 'preact';
import { observer } from 'mobx-preact';

/**
 * Sparkline component - displays a mini bar chart for trend visualization
 *
 * Props:
 * - data: Array of objects with a value field (e.g., [{week: '2024-W50', views: 100}, ...])
 * - valueField: String - the field name to use for values (default: 'views')
 * - height: Number - height in pixels (default: 20)
 * - barWidth: Number - width of each bar in pixels (default: 3)
 * - barGap: Number - gap between bars in pixels (default: 1)
 * - color: String - bar color (default: '#4e79a7')
 * - maxBars: Number - maximum number of bars to show (default: 8)
 * - showTooltip: Boolean - show tooltip on hover (default: true)
 * - className: String - additional CSS classes
 */
@observer
class Sparkline extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hoveredIndex: null,
        };
    }

    handleMouseEnter = (index) => {
        this.setState({ hoveredIndex: index });
    }

    handleMouseLeave = () => {
        this.setState({ hoveredIndex: null });
    }

    render() {
        const {
            data = [],
            valueField = 'views',
            height = 20,
            barWidth = 3,
            barGap = 1,
            color = '#4e79a7',
            maxBars = 8,
            showTooltip = true,
            className = '',
        } = this.props;

        const { hoveredIndex } = this.state;

        if (!data || data.length < 1) {
            return null;
        }

        // Get the last N items (or all if maxBars is 0/null)
        const displayData = maxBars > 0 ? data.slice(-maxBars) : data;

        // Calculate max value for scaling
        const values = displayData.map(d => d[valueField] || 0);
        const maxValue = Math.max(...values, 1); // Ensure at least 1 to avoid division by zero

        // Calculate total width
        const totalWidth = displayData.length * barWidth + (displayData.length - 1) * barGap;

        return (
            <div
                class={`d-inline-flex align-items-end position-relative ${className}`}
                style={`height: ${height}px; gap: ${barGap}px;`}
                title={showTooltip && hoveredIndex === null ? `${displayData.length} data points` : ''}
            >
                {displayData.map((item, index) => {
                    const value = item[valueField] || 0;
                    const barHeight = maxValue > 0 ? (value / maxValue) * 100 : 0;
                    const minHeight = value > 0 ? 10 : 0; // Minimum 10% height if there's any value
                    const finalHeight = Math.max(barHeight, minHeight);

                    return (
                        <div
                            key={index}
                            style={`
                                width: ${barWidth}px;
                                height: ${finalHeight}%;
                                min-height: ${value > 0 ? '2px' : '0'};
                                background: ${color};
                                border-radius: 1px;
                                transition: opacity 0.2s;
                                opacity: ${hoveredIndex !== null && hoveredIndex !== index ? 0.5 : 1};
                                cursor: ${showTooltip ? 'pointer' : 'default'};
                            `}
                            onMouseEnter={() => this.handleMouseEnter(index)}
                            onMouseLeave={this.handleMouseLeave}
                            title={showTooltip ? `${item.week || (item.hour !== undefined ? `${item.hour}:00` : `#${index + 1}`)}: ${value}` : ''}
                        />
                    );
                })}
            </div>
        );
    }
}

export default Sparkline;
